import { boot } from 'quasar/wrappers';
import axios, { AxiosInstance, AxiosError, AxiosRequestTransformer } from 'axios';
import { useUserStore } from '../stores/userStore';
import { WarningMessage } from '../notifications/warningmessage'
import { DateFormatter } from 'src/utils/dateUtils';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance;
  }
}

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
const api = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? 'https://localhost:5001' : ''
});

export default boot(({ app, router }) => {
  // for use inside Vue files (Options API) through this.$axios and this.$api

  app.config.globalProperties.$axios = axios;
  // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
  //       so you won't necessarily have to import axios in each vue file

  app.config.globalProperties.$api = api;
  // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
  //       so you can easily perform requests against your app's API

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 403) {
        const userStore = useUserStore();
        if (userStore.isAuthenticated && userStore.isUser) {
          await router.push('/');
        } else {
          await router.push('/forbidden');
        }
      }
      if (error.response?.status === 401) {
        await router.push('/login');
      }
      if (error.response?.status === 500) {
        WarningMessage.show('Wystąpił błąd podczas komunikacji z serwerem.')
      }
      return Promise.reject(error);
    }
  );
});

const isPlainObject = (v: unknown) =>
  Object.prototype.toString.call(v) === '[object Object]';

const tryToChangeTimeZone = (data: unknown): unknown => {
  if (data instanceof Date) {
    return DateFormatter.changeTimeZone(data, 'Europe/Warsaw');
  }

  return data;
}

const dateTransformer: AxiosRequestTransformer = (data) => {
  if (data instanceof Date) {
    return tryToChangeTimeZone(data);
  }
  if (Array.isArray(data)) {
    return data.map((val) => tryToChangeTimeZone(val));
  }
  if (isPlainObject(data)) {
    const newObj: {[k: string]: unknown} = {};
    for (const [key, val] of Object.entries(data)) {
      newObj[key] = tryToChangeTimeZone(val);
    }

    return newObj;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return data;
};

api.interceptors.request.use((config) => {
  config.transformRequest = [dateTransformer].concat(config.transformRequest as []);
  return config;
}, (error) => Promise.reject(error));

export { api };
